import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Field, TextInput, SelectInput, ActionButton } from "./common";
import { RequesterPicker } from "./RequesterPicker";

const STAGE_OPTIONS = [
  { value: "A", label: "Stage A" },
  { value: "B", label: "Stage B" },
  { value: "C", label: "Stage C" },
  { value: "D", label: "Stage D" },
];

export const WellBillingEditor = ({ dispatch, onSaved, showRequester = true, testId = "well-billing" }) => {
  const { user } = useAuth();
  const wells = dispatch.well_names || [];
  const build = () => wells.map((w) => {
    const ex = (dispatch.well_billing || []).find((b) => b.well === w) || {};
    return { well: w, start_date: ex.start_date || "", end_date: ex.end_date || "", stage: ex.stage || "" };
  });
  const [rows, setRows] = useState(build);
  const [requesterId, setRequesterId] = useState(dispatch.requester_id || "");
  const [companyMan, setCompanyMan] = useState(dispatch.company_man || "");
  const [salesRep, setSalesRep] = useState(dispatch.sales_rep || dispatch.created_by_name || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRows(build());
    setRequesterId(dispatch.requester_id || "");
    setCompanyMan(dispatch.company_man || "");
    setSalesRep(dispatch.sales_rep || dispatch.created_by_name || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch.id]);

  const setRow = (i, patch) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const save = async () => {
    const bad = rows.find((r) => r.start_date && r.end_date && r.start_date > r.end_date);
    if (bad) { toast.error(`${bad.well}: start date is after end date`); return; }
    setBusy(true);
    try {
      const payload = { sales_rep: salesRep.trim() || null };
      if (wells.length) payload.well_billing = rows.map((r) => ({ well: r.well, start_date: r.start_date || null, end_date: r.end_date || null, stage: r.stage || null }));
      if (showRequester) payload.requester_id = requesterId || null;
      const { data } = await api.patch(`/fractank/dispatches/${dispatch.id}`, payload);
      toast.success("Tank days saved");
      onSaved?.(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid={`${testId}-editor`}>
      {showRequester && (
        <RequesterPicker
          testId={testId}
          customerId={dispatch.customer_id}
          value={requesterId}
          snapshotName={companyMan}
          onChange={(r) => { setRequesterId(r?.id || ""); setCompanyMan(r?.name || ""); }}
        />
      )}
      <Field label="Requesting sales rep">
        <div className="flex gap-2 min-w-0">
          <TextInput testId={`${testId}-rep`} value={salesRep} onChange={(e) => setSalesRep(e.target.value)} placeholder="Rep who requested this" className="flex-1 min-w-0" />
          {user?.name && <ActionButton type="button" variant="secondary" testId={`${testId}-rep-claim`} onClick={() => setSalesRep(user.name)} className="!text-xs whitespace-nowrap min-h-11">Use my name</ActionButton>}
        </div>
      </Field>
      <div className="space-y-3">
        <p className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">Tank Days per Well</p>
        {wells.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid={`${testId}-empty`}>No wells on this ticket. Add a location with wells to track tank days per well.</p>
        ) : rows.map((r, i) => (
          <div key={r.well} data-testid={`${testId}-row-${i}`} className="rounded-md border border-slate-700 p-3 space-y-2">
            <p className="text-sm font-medium text-slate-100 [overflow-wrap:anywhere]" data-testid={`${testId}-well-${i}`}>{r.well}</p>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Start date"><TextInput testId={`${testId}-start-${i}`} type="date" value={r.start_date} onChange={(e) => setRow(i, { start_date: e.target.value })} /></Field>
              <Field label="End date"><TextInput testId={`${testId}-end-${i}`} type="date" value={r.end_date} onChange={(e) => setRow(i, { end_date: e.target.value })} /></Field>
            </div>
            <Field label="Stage (optional)"><SelectInput testId={`${testId}-stage-${i}`} value={r.stage} onChange={(e) => setRow(i, { stage: e.target.value })} placeholder="No stage" options={STAGE_OPTIONS} /></Field>
          </div>
        ))}
      </div>
      <ActionButton type="button" testId={`${testId}-save`} onClick={save} disabled={busy} className="w-full justify-center">{busy ? "Saving…" : "Save"}</ActionButton>
    </div>
  );
};
